import { useState } from "react"
import { Plus } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { postInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
import { DishIllustration } from "./dish-illustration"
import { DishPrice, DishPromotionBadge } from "./dish-price"
import { usePickupCart } from "./pickup-cart"
import { usePickupHours } from "./use-pickup-hours"
import type { MenuItem, OrderServiceType } from "./types"
import { t } from "@/lib/i18n"

type DishListItemProps = {
  item: MenuItem
  onSelect: (item: MenuItem) => void
  serviceType?: OrderServiceType
  /** The pre-seating menu: no quick-add, browsing the row still opens the detail. */
  readOnly?: boolean
}

export function DishListItem({
  item,
  onSelect,
  serviceType = "eat_in",
  readOnly = false,
}: DishListItemProps) {
  const unavailable = item.available === false
  const [adding, setAdding] = useState(false)
  // Same fork `useDishOrder` makes, and it has to be made here too: this button
  // posts on its own rather than going through the hook, so on a pickup menu it
  // was sending the dish to /orders — the dine-in endpoint, which has no visit
  // to attach it to and bounced the diner to the dashboard.
  const pickupCart = usePickupCart()
  // A closed kitchen takes no orders, so the quick-add stops offering to
  // place one. False on a dine-in menu, where staff seated the diner and the
  // opening hours have nothing to say about their table.
  const { closed: pickupClosed } = usePickupHours()

  // A required group (minSelect > 0) can't be satisfied by a bare quick-add —
  // the order builder would reject it — so the "+" opens the detail to pick
  // extras instead of posting. Only items with no required group quick-add.
  const needsChoice = item.extraGroups?.some((group) => group.minSelect > 0) ?? false

  // The diner now stays on the menu after ordering, so the button is still under
  // their thumb: without this guard a double tap posts two orders.
  const handleQuickAdd = () => {
    if (adding) return

    if (pickupCart) {
      pickupCart.add(item, { quantity: 1, extraIds: [], notes: null })
      toast.success(t("ui.react.features.user.pickup_cart.added"))
      return
    }

    setAdding(true)
    postInline(
      "/dinein/orders",
      { order: { item_id: item.id, quantity: 1, service_type: serviceType } },
      { preserveState: true }
    )
      .then(toastInlineUpdateSuccess)
      .catch(toastInlineUpdateError)
      .finally(() => setAdding(false))
  }

  return (
    <div
      data-testid={`dish-list-item-${item.id}`}
      className="flex items-center gap-3 border-b border-border/60 py-3 last:border-b-0"
    >
      <button
        type="button"
        onClick={() => onSelect(item)}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="size-14 shrink-0 overflow-hidden rounded-xl">
          <DishIllustration src={item.imageUrl} emoji={item.emoji} alt={item.name} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold leading-tight tracking-tight">
            {item.name}
          </span>
          {item.description && (
            <span className="block truncate text-xs leading-snug text-muted-foreground">
              {item.description}
            </span>
          )}
          {/* The rule's name above the figures, not beside them: the row is
              already a truncating three-line block, and a badge competing with
              the price for the same line is the first thing to lose its text. */}
          <DishPromotionBadge item={item} className="mt-1 flex w-fit" />
          <DishPrice item={item} className="mt-1 flex" />
          {unavailable && (
            <span className="mt-1 block text-xs font-medium text-muted-foreground">
              {t("ui.user.dish.unavailable")}
            </span>
          )}
        </span>
      </button>
      {!readOnly && (
        <Button
          type="button"
          size="icon"
          className="shrink-0 rounded-full"
          onClick={needsChoice ? () => onSelect(item) : handleQuickAdd}
          disabled={unavailable || adding || pickupClosed}
          data-testid={`dish-quick-add-${item.id}`}
          aria-label={t("ui.react.features.user.dish_list_item.add", { name: item.name })}
        >
          <Plus />
        </Button>
      )}
    </div>
  )
}
